import { useEffect, useState } from "react";
import { FileText, Download, CheckCircle2, XCircle, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const TYPE_LABELS = {
  contract: { de: "Vertrag", fr: "Contrat", it: "Contratto", en: "Contract" },
  proof: { de: "Nachweis", fr: "Justificatif", it: "Prova", en: "Proof" },
  certificate: { de: "Zertifikat", fr: "Certificat", it: "Certificato", en: "Certificate" },
  upload: { de: "Upload", fr: "Fichier", it: "Caricamento", en: "Upload" },
  customer_doc: { de: "Kundenunterlage", fr: "Doc. client", it: "Doc. cliente", en: "Customer doc" },
  provider_doc: { de: "Anbieterunterlage", fr: "Doc. prestataire", it: "Doc. fornitore", en: "Provider doc" },
  internal_admin: { de: "Intern (Admin)", fr: "Interne (admin)", it: "Interno (admin)", en: "Internal (admin)" },
  review_approval: { de: "Prüf-/Freigabe", fr: "Vérification", it: "Verifica", en: "Review/approval" },
};
const STATUS_LABELS = {
  draft: { de: "Entwurf", fr: "Brouillon", it: "Bozza", en: "Draft" },
  active: { de: "Aktiv", fr: "Actif", it: "Attivo", en: "Active" },
  pending_review: { de: "Zur Prüfung", fr: "À vérifier", it: "In verifica", en: "Pending review" },
  approved: { de: "Freigegeben", fr: "Approuvé", it: "Approvato", en: "Approved" },
  rejected: { de: "Abgelehnt", fr: "Rejeté", it: "Rifiutato", en: "Rejected" },
  expired: { de: "Abgelaufen", fr: "Expiré", it: "Scaduto", en: "Expired" },
  archived: { de: "Archiviert", fr: "Archivé", it: "Archiviato", en: "Archived" },
};
const STATUS_COLORS = {
  approved: "#34d399", active: "#34d399", pending_review: "#fbbf24",
  rejected: "#fb7185", expired: "#fb7185", draft: "#a1a1aa", archived: "#a1a1aa",
};

function fmtSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export function ControlDocuments({ portals = [] }) {
  const { L, lang } = useI18n();
  const [items, setItems] = useState(null);
  const [portalFilter, setPortalFilter] = useState("");
  const [checking, setChecking] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/control/documents", { params: portalFilter ? { portal_key: portalFilter } : {} });
      setItems(data);
    } catch (e) {
      setItems([]);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [portalFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const runCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/control/documents/run-expiry-check");
      toast.success(L({
        de: `Geprüft: ${data.expired} abgelaufen, ${data.reminded} Erinnerungen`,
        fr: `Vérifié : ${data.expired} expirés, ${data.reminded} rappels`,
        it: `Verificato: ${data.expired} scaduti, ${data.reminded} promemoria`,
        en: `Checked: ${data.expired} expired, ${data.reminded} reminders`,
      }));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setChecking(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob", params: { portal_key: d.portal_key } });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename || d.title; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (d, status) => {
    try { await api.patch(`/documents/${d.id}`, { status }, { params: { portal_key: d.portal_key } }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (d) => {
    if (!window.confirm(L({ de: `Dokument „${d.title}" wirklich löschen?`, fr: `Supprimer « ${d.title} » ?`, it: `Eliminare «${d.title}»?`, en: `Delete "${d.title}"?` }))) return;
    try { await api.delete(`/documents/${d.id}`, { params: { portal_key: d.portal_key } }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="control-documents">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-zinc-400">{items ? items.length : "…"} {L({ de: "Dokumente über alle Portale", fr: "documents sur tous les portails", it: "documenti su tutti i portali", en: "documents across all portals" })}</p>
        <div className="flex items-center gap-2">
          <button data-testid="control-documents-expiry-check" onClick={runCheck} disabled={checking}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm text-zinc-200 disabled:opacity-50" style={{ borderColor: "var(--border)" }}>
            <Clock className="h-3.5 w-3.5" /> {L({ de: "Ablauf-Prüfung", fr: "Vérif. expiration", it: "Verifica scadenze", en: "Expiry check" })}
          </button>
          <select data-testid="control-documents-portal-filter" value={portalFilter} onChange={(e) => setPortalFilter(e.target.value)}
            className="h-9 rounded-lg border bg-zinc-900 px-3 text-sm text-white" style={{ borderColor: "var(--border)" }}>
            <option value="">{L({ de: "Alle Portale", fr: "Tous les portails", it: "Tutti i portali", en: "All portals" })}</option>
            {portals.map((p) => <option key={p.portal_key} value={p.portal_key}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {!items ? (
        <div className="py-16 text-center text-sm text-zinc-500">…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed py-16 text-center text-zinc-500" style={{ borderColor: "var(--border)" }} data-testid="control-documents-empty">
          <FileText className="mx-auto h-8 w-8 opacity-40" />
          <p className="mt-3 text-sm">{L({ de: "Noch keine Dokumente vorhanden", fr: "Aucun document", it: "Nessun documento", en: "No documents yet" })}</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map((d) => (
            <div key={d.id} data-testid={`control-document-${d.id}`} className="flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center"
              style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-zinc-800 text-zinc-300"><FileText className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-semibold text-white">{d.title}</span>
                  <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-300">{d.portal_key}</span>
                  <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-400">{L(TYPE_LABELS[d.document_type]) || d.document_type}</span>
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest" style={{ background: "rgba(255,255,255,0.06)", color: STATUS_COLORS[d.status] || "#a1a1aa" }}>{L(STATUS_LABELS[d.status]) || d.status}</span>
                </div>
                <div className="mt-1 text-xs text-zinc-500">{fmtSize(d.file_size)} · {d.owner_type} · {d.uploaded_by_name || "—"} · {new Date(d.created_at).toLocaleDateString(lang)}</div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button data-testid={`control-document-download-${d.id}`} onClick={() => download(d)} className="inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs text-zinc-200" style={{ borderColor: "var(--border)" }}><Download className="h-3.5 w-3.5" /></button>
                {d.status === "pending_review" && (
                  <>
                    <button data-testid={`control-document-approve-${d.id}`} onClick={() => setStatus(d, "approved")} className="inline-flex h-9 items-center rounded-lg px-2.5 text-emerald-400" style={{ background: "rgba(16,185,129,0.1)" }}><CheckCircle2 className="h-4 w-4" /></button>
                    <button data-testid={`control-document-reject-${d.id}`} onClick={() => setStatus(d, "rejected")} className="inline-flex h-9 items-center rounded-lg px-2.5 text-rose-400" style={{ background: "rgba(244,63,94,0.1)" }}><XCircle className="h-4 w-4" /></button>
                  </>
                )}
                <button data-testid={`control-document-delete-${d.id}`} onClick={() => remove(d)} className="inline-flex h-9 items-center rounded-lg bg-zinc-800 px-2.5 text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
